import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import api from '../utils/api';
import { PmLoading, PmNotice } from '../components/pm/PmStates';
import { BUSINESS } from '../data/pmContent';

const Field = ({ id, label, error, children }) => (
  <div className="field">
    <label htmlFor={id}>{label}</label>
    {children}
    <span className="field__error" role="alert">{error || ''}</span>
  </div>
);

const AccountSettings = () => {
  const { user, loading } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState(null); // { variant, message }

  const { register, handleSubmit, formState: { errors } } = useForm({
    values: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const registerOptions = {
    name: {
      required: 'El nombre es requerido',
      minLength: { value: 3, message: 'El nombre debe tener al menos 3 caracteres' },
    },
    email: {
      required: 'El email es requerido',
      pattern: {
        value: /^[A-z0-9._%+-]+@[A-z0-9.-]+\.[A-z]{2,4}$/,
        message: 'El email no es válido',
      },
    },
    phone: {
      pattern: { value: /^[0-9]*$/, message: 'El teléfono no es válido' },
    },
  };

  const {
    register: registerPwd,
    handleSubmit: handleSubmitPwd,
    formState: { errors: pwdErrors },
    watch,
    reset: resetPwd,
  } = useForm();
  const newPassword = watch('new_password');

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setNotice(null);
      await api.put('/user/profile', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });
      setNotice({ variant: 'success', message: 'Tu perfil ha sido actualizado correctamente.' });
    } catch (err) {
      setNotice({ variant: 'danger', message: getErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onPasswordSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setNotice(null);
      await api.put('/user/password', {
        current_password: formData.current_password,
        password: formData.new_password,
        password_confirmation: formData.new_password_confirmation,
      });
      setNotice({ variant: 'success', message: 'Tu contraseña ha sido actualizada correctamente.' });
      resetPwd();
    } catch (err) {
      setNotice({ variant: 'danger', message: getErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  const initial = (user?.name || '?').charAt(0).toUpperCase();

  return (
    <div className="pm-scope account">
      <Helmet>
        <title>Mi cuenta - {BUSINESS.name}</title>
      </Helmet>

      <section className="section">
        <div className="container container--narrow">
          <header className="account__head">
            <div>
              <span className="eyebrow">Tu cuenta</span>
              <h1>Mi cuenta</h1>
              <p>Actualiza tus datos de contacto y tu contraseña.</p>
            </div>
          </header>

          {(loading || !user) ? (
            <PmLoading message="Cargando tu perfil…" />
          ) : (
            <>
              {notice && (
                <PmNotice variant={notice.variant} onClose={() => setNotice(null)}>{notice.message}</PmNotice>
              )}

              <div className="acct-grid acct-grid--2">
                <article className="acct-card">
                  <div className="acct-card__top" style={{ alignItems: 'center' }}>
                    <h2>Datos personales</h2>
                    <span className="review__avatar" aria-hidden="true">{initial}</span>
                  </div>
                  <form className="acct-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Field id="accountName" label="Nombre completo" error={errors?.name?.message}>
                      <input
                        id="accountName"
                        type="text"
                        autoComplete="name"
                        aria-invalid={!!errors?.name}
                        {...register('name', registerOptions.name)}
                      />
                    </Field>
                    <Field id="accountEmail" label="Correo electrónico" error={errors?.email?.message}>
                      <input
                        id="accountEmail"
                        type="email"
                        autoComplete="email"
                        aria-invalid={!!errors?.email}
                        {...register('email', registerOptions.email)}
                      />
                    </Field>
                    <Field id="accountPhone" label="Teléfono" error={errors?.phone?.message}>
                      <input
                        id="accountPhone"
                        type="tel"
                        autoComplete="tel"
                        inputMode="numeric"
                        aria-invalid={!!errors?.phone}
                        {...register('phone', registerOptions.phone)}
                      />
                    </Field>
                    <button className="btn btn--primary" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <><span className="pm-spinner" aria-hidden="true" /> Guardando…</> : 'Guardar cambios'}
                    </button>
                  </form>
                </article>

                <article className="acct-card">
                  <h2>Cambiar contraseña</h2>
                  <form className="acct-form" onSubmit={handleSubmitPwd(onPasswordSubmit)} noValidate>
                    <Field id="currentPassword" label="Contraseña actual" error={pwdErrors?.current_password?.message}>
                      <input
                        id="currentPassword"
                        type="password"
                        autoComplete="current-password"
                        placeholder="Contraseña actual"
                        aria-invalid={!!pwdErrors?.current_password}
                        {...registerPwd('current_password', { required: 'La contraseña actual es requerida' })}
                      />
                    </Field>
                    <Field id="newPassword" label="Nueva contraseña" error={pwdErrors?.new_password?.message}>
                      <input
                        id="newPassword"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Mínimo 8 caracteres"
                        aria-invalid={!!pwdErrors?.new_password}
                        {...registerPwd('new_password', {
                          required: 'La nueva contraseña es requerida',
                          minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                        })}
                      />
                    </Field>
                    <Field id="newPasswordConfirm" label="Confirmar nueva contraseña" error={pwdErrors?.new_password_confirmation?.message}>
                      <input
                        id="newPasswordConfirm"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Repite la nueva contraseña"
                        aria-invalid={!!pwdErrors?.new_password_confirmation}
                        {...registerPwd('new_password_confirmation', {
                          required: 'Debes confirmar la contraseña',
                          validate: (value) => value === newPassword || 'Las contraseñas no coinciden',
                        })}
                      />
                    </Field>
                    <button className="btn btn--secondary" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <><span className="pm-spinner" aria-hidden="true" /> Cambiando…</> : 'Cambiar contraseña'}
                    </button>
                  </form>
                </article>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default AccountSettings;
